import { useTranslation } from 'contexts/Localization'
import React, { useState, useEffect } from 'react'
import styled, { useTheme as useStyledTheme } from 'styled-components'
import { Flex, Text, ArrowForwardIcon, Rocket2, Skeleton, InfoIcon, useTooltip } from 'vvs-uikit'
import { useFarmBoostFactor } from 'state/farmBoost/useFarmBoostFactor'
import {
  calcPotentialFarmBoostFactor,
  PotentialFarmBoostFactorResult,
} from 'state/farmBoost/calcPotentialFarmBoostFactor'
import { DeserializedFarm } from 'state/types'
import { TokenPairText, TokenWrapper } from 'views/BoostCalculator/styles'
import { TokenPairImage } from 'components/TokenImage'
import { getDisplayApr, getBoostedDisplayApr } from 'views/Farms/utils'

const ApplyText = styled(Text)`
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`

export const CurrentBoostMultiplierRow: React.FC<{
  farm: DeserializedFarm
  vvsRewardsApr?: number
  tokenRewardsApr?: number
  lpRewardsApr?: number
  showApplyToBtn?: boolean
}> = ({ farm, vvsRewardsApr, tokenRewardsApr, lpRewardsApr, showApplyToBtn = true }) => {
  const { colors } = useStyledTheme()
  const { t } = useTranslation()
  const xVVSBoostFactorData = useFarmBoostFactor(farm.pid)
  const isLoading = !xVVSBoostFactorData.loaded

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t(
      'Your APR% remain the same because you do not have any staked liquidity in the selected farm.  You can continue applying the boost and stake liquidity in this farm later to enjoy the boost APR%',
    ),
    { placement: 'top-end', tooltipOffset: [20, 10] },
  )

  const boostedApr = getBoostedDisplayApr(
    vvsRewardsApr,
    tokenRewardsApr,
    lpRewardsApr,
    xVVSBoostFactorData?.boostFactor,
    1,
    1,
  )

  return (
    <Flex flexDirection="column">
      <Flex justifyContent="space-between" flexDirection="row" gap="10px">
        <Flex alignItems="center" justifyContent="center">
          <TokenWrapper>
            <TokenPairImage
              primaryToken={farm.token}
              secondaryToken={farm.quoteToken}
              variant="inverted"
              width={40}
              height={40}
            />
          </TokenWrapper>
          <TokenPairText>{farm.lpSymbol.split(' ')[0]}</TokenPairText>
        </Flex>
        <Flex alignItems="center" gap="5px" justifyContent="center">
          <Rocket2 width={16} height={16} color={colors.text} />

          <AprBeforeBoostText tokenRewardsApr={tokenRewardsApr} lpRewardsApr={lpRewardsApr} />
          <ArrowForwardIcon width="24px" height="24px" color={colors.text} />
          {isLoading || Number.isNaN(boostedApr) ? (
            <Skeleton width="40px" height="24px" />
          ) : (
            <Text fontSize="16px" fontWeight="600" lineHeight="24px">
              {`${boostedApr}%`}
            </Text>
          )}
          {!isLoading && xVVSBoostFactorData?.boostFactor === 1 && (
            <Flex ref={targetRef}>
              <InfoIcon color="primary" />
              {tooltipVisible && tooltip}
            </Flex>
          )}
        </Flex>
      </Flex>
      {showApplyToBtn && (
        <Flex justifyContent="flex-end" alignItems="center">
          <ApplyText>{t('Apply to another farm')}</ApplyText>
        </Flex>
      )}
    </Flex>
  )
}

export const PreviewBoostMultiplierRow: React.FC<{
  farm: DeserializedFarm
  vvsRewardsApr?: number
  tokenRewardsApr?: number
  lpRewardsApr?: number
  showApplyToBtn?: boolean
  boostingNfts?: []
}> = ({ farm, vvsRewardsApr, tokenRewardsApr, lpRewardsApr, showApplyToBtn = true, boostingNfts = [] }) => {
  const { colors } = useStyledTheme()
  const { t } = useTranslation()

  const [boostFactor, setBoostFactor] = useState<PotentialFarmBoostFactorResult>(null)

  useEffect(() => {
    let latest = true
    setBoostFactor(null)
    calcPotentialFarmBoostFactor(farm.pid, {
      nfts: boostingNfts,
    }).then(async (calculatedBoostFactor) => {
      if (!latest) return

      setBoostFactor(calculatedBoostFactor)
    })

    return () => {
      latest = false
    }
  }, [farm, boostingNfts])

  const boostedApr = getBoostedDisplayApr(vvsRewardsApr, tokenRewardsApr, lpRewardsApr, boostFactor?.boostFactor, 1, 1)

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t(
      'Your APR% remain the same because you do not have any staked liquidity in the selected farm.  You can continue applying the boost and stake liquidity in this farm later to enjoy the boost APR%',
    ),
    { placement: 'top-end', tooltipOffset: [20, 10] },
  )

  return (
    <Flex flexDirection="column">
      <Flex justifyContent="space-between" flexDirection="row" gap="10px">
        <Flex alignItems="center" justifyContent="center">
          <TokenWrapper>
            <TokenPairImage
              primaryToken={farm.token}
              secondaryToken={farm.quoteToken}
              variant="inverted"
              width={40}
              height={40}
            />
          </TokenWrapper>
          <TokenPairText>{farm.lpSymbol.split(' ')[0]}</TokenPairText>
        </Flex>
        <Flex alignItems="center" gap="5px" justifyContent="center">
          <Rocket2 width={16} height={16} color={colors.text} />

          <AprBeforeBoostText tokenRewardsApr={tokenRewardsApr} lpRewardsApr={lpRewardsApr} />
          <ArrowForwardIcon width="24px" height="24px" color={colors.text} />
          {!boostFactor ? (
            <Skeleton width="40px" height="24px" />
          ) : (
            <Text fontSize="16px" fontWeight="600" lineHeight="24px">
              {`${boostedApr}%`}
            </Text>
          )}
          {boostFactor?.boostFactor === 1 && (
            <Flex ref={targetRef}>
              <InfoIcon color="primary" />
              {tooltipVisible && tooltip}
            </Flex>
          )}
        </Flex>
      </Flex>
      {showApplyToBtn && (
        <Flex justifyContent="flex-end" alignItems="center">
          <ApplyText>{t('Apply to another farm')}</ApplyText>
        </Flex>
      )}
    </Flex>
  )
}

const AprBeforeBoostText = ({ tokenRewardsApr, lpRewardsApr }: { tokenRewardsApr?: number; lpRewardsApr?: number }) => {
  return (
    <Text fontSize="16px" fontWeight="600" lineHeight="24px">
      {typeof tokenRewardsApr === 'number' && typeof lpRewardsApr === 'number' ? (
        `${getDisplayApr(tokenRewardsApr, lpRewardsApr, 1)}%`
      ) : (
        <Skeleton width="40px" height="24px" />
      )}
    </Text>
  )
}
